<template>
  <q-dialog v-model="dialog" :persistent="!actionsCompleted" @before-hide="beforeHide">
    <div class="dialog bg-white-1 q-pa-md">
      <Grid justify-content="end" class="q-mb-md">
        <svg
          class="cursor-pointer"
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          @click="dialog = false"
        >
          <path
            d="M13.725 4.28254C13.4325 3.99004 12.96 3.99004 12.6675 4.28254L9.00001 7.94254L5.33251 4.27504C5.04001 3.98254 4.56751 3.98254 4.27501 4.27504C3.98251 4.56754 3.98251 5.04004 4.27501 5.33254L7.94251 9.00004L4.27501 12.6675C3.98251 12.96 3.98251 13.4325 4.27501 13.725C4.56751 14.0175 5.04001 14.0175 5.33251 13.725L9.00001 10.0575L12.6675 13.725C12.96 14.0175 13.4325 14.0175 13.725 13.725C14.0175 13.4325 14.0175 12.96 13.725 12.6675L10.0575 9.00004L13.725 5.33254C14.01 5.04754 14.01 4.56754 13.725 4.28254Z"
            fill="#55597A"
          />
        </svg>
      </Grid>

      <Grid gap="var(--r-space-xxl)" class="q-pb-xl text-center">
        <Grid v-if="accepted" gap="var(--r-space-sm)" justify-items="center" class="text-center">
          <DoneIcon />
          <div class="text-body-1 text-secondary-1">
            You have accepted {{ acceptedIds.length }}
            {{ acceptedIds.length > 1 ? 'subscriptions' : 'subscription' }}
          </div>
        </Grid>

        <Grid v-else-if="acceptedIds.length" class="text-center" gap="var(--r-space-md)">
          <div class="text-body-3 text-secondary-1">
            Are you sure you want to accept {{ acceptedIds.length }}
            {{ acceptedIds.length > 1 ? 'subscriptions?' : 'subscription?' }}
          </div>

          <Grid gaf="column" gap="var(--r-space-md)" justify-content="center">
            <RBtn :disable="acceptLoading || cancelLoading" type="ghost" @click="dialog = false">no, maybe later</RBtn>

            <RBtn :loading="acceptLoading" @click="accept">
              yes, accept {{ acceptedIds.length }}
              {{ acceptedIds.length > 1 ? 'subscriptions' : 'subscription' }}
            </RBtn>
          </Grid>
        </Grid>

        <Grid v-if="cancelled" gap="var(--r-space-sm)" justify-items="center" class="text-center">
          <DoneIcon />
          <div class="text-body-1 text-secondary-1">
            You have cancelled {{ cancelledIds.length }}
            {{ cancelledIds.length > 1 ? 'subscriptions' : 'subscription' }}
          </div>
        </Grid>

        <Grid v-else-if="cancelledIds.length" class="text-center" gap="var(--r-space-md)">
          <div class="text-body-3 text-secondary-1">
            Are you sure you want to cancel {{ cancelledIds.length }}
            {{ cancelledIds.length > 1 ? 'subscriptions?' : 'subscription?' }}
          </div>

          <Grid gaf="column" gap="var(--r-space-md)" justify-content="center">
            <RBtn :disable="acceptLoading || cancelLoading" type="ghost" @click="dialog = false">no, maybe later</RBtn>

            <RBtn :loading="cancelLoading" type="tertiary" @click="cancel">
              yes, cancel {{ cancelledIds.length }}
              {{ cancelledIds.length > 1 ? 'subscriptions' : 'subscription' }}
            </RBtn>
          </Grid>
        </Grid>

        <RBtn v-if="actionsCompleted" type="text" size="small" @click="dialog = false">Close</RBtn>
      </Grid>
    </div>
  </q-dialog>
</template>

<script>
import * as apiTo from '@/modular-api'
// Components
import DoneIcon from '@/ui/icons/DoneIcon'

export default {
  components: {
    DoneIcon
  },

  props: {
    value: { type: Boolean, required: true }
  },

  data: () => ({
    acceptLoading: false,
    cancelLoading: false,
    accepted: false,
    cancelled: false,
    loadModuleState: false
  }),

  computed: {
    dataForBatchDecisionMaking() {
      if (!this.loadModuleState) return null
      return this.$store.state['renewal-proposal'].dataForBatchDecisionMaking
    },

    dialog: {
      get() {
        return this.value
      },

      set(newVal) {
        this.$emit('input', newVal)
      }
    },

    acceptedIds() {
      if (!this.dataForBatchDecisionMaking) return []
      return this.dataForBatchDecisionMaking.find(d => d.name === 'to accept')?.subscription_period_ids ?? []
    },

    cancelledIds() {
      if (!this.dataForBatchDecisionMaking) return []
      return this.dataForBatchDecisionMaking.find(d => d.name === 'to cancel')?.subscription_period_ids ?? []
    },

    actionsCompleted() {
      if (this.acceptedIds.length && this.cancelledIds.length && this.accepted && this.cancelled) return true
      if (this.acceptedIds.length && !this.cancelledIds.length && this.accepted) return true
      if (this.cancelledIds.length && !this.acceptedIds.length && this.cancelled) return true
      return false
    }
  },

  watch: {
    value(newVal) {
      if (newVal) this.loadModuleState = true
    },

    dataForBatchDecisionMaking(newVal, oldVal) {
      if (oldVal) this.accepted = this.cancelled = false
    }
  },

  methods: {
    beforeHide() {
      if (this.accepted || this.cancelled) {
        this.$store.commit('renewal-proposal/refreshTable')
        this.$store.commit('renewal-proposal/refreshCards')
      }
    },

    accept() {
      this.acceptLoading = true
      apiTo
        .ACCEPT_SUBSCRIPTION_PERIODS({ ids: this.acceptedIds })
        .then(() => (this.accepted = true))
        .finally(() => (this.acceptLoading = false))
    },

    cancel() {
      this.cancelLoading = true
      apiTo
        .CANCEL_SUBSCRIPTION_PERIODS({ ids: this.cancelledIds })
        .then(() => (this.cancelled = true))
        .finally(() => (this.cancelLoading = false))
    }
  }
}
</script>

<style lang="scss" scoped>
.dialog {
  width: 475px;
}
</style>
